// AwaitingApprovalRail — the design's "Awaiting approval" horizontal rail
// (my-access.jsx AwaitingApprovalRail / .awaiting-rail / .awaiting-card).
//
// One card per in-flight request the current member submitted. Pending cards
// show who they're waiting on + a Nudge button; approved/provisioning cards
// show a "setting up access" state. A draft whose policy wants the requester's
// passkey (`awaiting_signature`) shows "Needs your signature" and a Sign
// button, and comes first. Data comes from MF_ACCESS.useListMyActiveRequests();
// nudging reuses useNudgeReviewers.

import { Box, Flex, Icon, Spinner, useToast } from "@chakra-ui/react";
import { useState } from "react";
import {
  TbBell,
  TbCheck,
  TbCircleCheck,
  TbShieldCheck,
  TbSignature,
  TbTrash,
} from "react-icons/tb";
import AppLogo from "components/ds/AppLogo";
import { appDisplayName } from "modules/apps/tenants";
import Avatar from "components/ds/Avatar";
import ConfirmModal from "components/ds/ConfirmModal";
import UtilityButton from "components/ds/UtilityButton";
import {
  MF_ACCESS,
  type MFMyActiveRequest,
  type MFRequestMember,
} from "state/members/access_v2/catalogs";
import { needsSignature } from "state/members/stepUp";
import { useSignRequest } from "pages/Members/StepUp/useRequestSigning";

export interface RailMember {
  firstName?: string;
  lastName?: string;
  imageUrl?: string;
}

const fullName = (m?: RailMember | MFRequestMember | null): string =>
  m ? [m.firstName, m.lastName].filter(Boolean).join(" ").trim() : "";

// "Request sent N days ago" — `today` for <1 day.
const sentLabel = (createdAt: string): string => {
  const days = Math.floor((Date.now() - new Date(createdAt).getTime()) / 86_400_000);
  if (days <= 0) return "Request sent today";
  return `Request sent ${days} ${days === 1 ? "day" : "days"} ago`;
};

// .awaiting-card__for-tag — pill showing whether the request is for the member
// themselves (neutral) or on someone else's behalf (azurite).
const ForTag = ({
  recipient,
  me,
}: {
  recipient: MFRequestMember | null;
  me?: RailMember;
}) => {
  const isOther = Boolean(recipient);
  const name = isOther ? fullName(recipient) || recipient!.email : "";

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      gap="1xs" // 6px
      pl="2xs" // 4px
      pr="10px"
      py="3px"
      borderRadius="full"
      fontSize="xs" // 12px
      fontWeight={500}
      whiteSpace="nowrap"
      maxW="200px"
      flexShrink={0}
      bg={isOther ? "azurite.50" : "neutral.100"}
      color={isOther ? "azurite.700" : "neutral.800"}
      title={isOther ? `On behalf of ${name}` : "For yourself"}
    >
      <Avatar
        size="xs"
        name={isOther ? name : fullName(me) || "Me"}
        src={isOther ? recipient!.imageUrl : me?.imageUrl}
      />
      <Box as="span" overflow="hidden" textOverflow="ellipsis">
        {isOther ? (
          <>
            <Box as="span" color="azurite.600" fontWeight={400}>
              On behalf of:
            </Box>{" "}
            {name}
          </>
        ) : (
          "For myself"
        )}
      </Box>
    </Box>
  );
};

const AwaitingCard = ({
  request: r,
  me,
  onOpen,
}: {
  request: MFMyActiveRequest;
  me?: RailMember;
  onOpen?: (request: MFMyActiveRequest) => void;
}) => {
  const toast = useToast();
  const { mutate: nudge, isLoading: nudging } = MF_ACCESS.useNudgeReviewers(r.id);
  const { mutate: cancel, isLoading: cancelling } = MF_ACCESS.useCancelRequest(
    r.id,
  );
  const { sign, isStarting: startingSignature } = useSignRequest();
  const [justNudged, setJustNudged] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  // The backend enforces a 24h nudge cooldown. Reflect an existing nudge
  // (last_nudged_at within the window) so the button starts disabled rather
  // than letting the click bounce off the server with a cooldown error.
  const recentlyNudged = r.lastNudgedAt
    ? Date.now() - new Date(r.lastNudgedAt).getTime() < 24 * 60 * 60 * 1000
    : false;
  const isNudged = justNudged || recentlyNudged;

  const appName = r.catalogItem?.name ?? r.catalog?.name ?? "";
  const itemName = appDisplayName({
    ...r.selectableResource,
    name: r.selectableResource?.name ?? r.catalogItem?.name ?? "Access request",
  });
  const logoName = r.catalogItem?.name || r.selectableResource?.name || "?";
  const logoUrl = r.catalogItem?.imageUrl || r.selectableResource?.imageUrl;

  const isPending = r.status === "pending";
  const isDraft = needsSignature(r);
  // Name a person only when there's exactly one approver. With more than one we
  // stay generic ("Awaiting approval") — listing names is noisy and implies
  // you're waiting on one specific person when it may be an any-of-N pool. With
  // none resolved, fall back to the stage name if there is one.
  const awaitingName =
    r.approvers.length === 1
      ? fullName(r.approvers[0]) || r.approvers[0].email
      : r.approvers.length === 0
        ? r.stageName || null
        : null;

  const doNudge = () => {
    setJustNudged(true);
    nudge()
      .then(() =>
        toast({ status: "success", variant: "subtle", title: "Reviewers nudged" }),
      )
      .catch(() => setJustNudged(false));
  };

  const doCancel = () => {
    cancel()
      .then(() =>
        toast({ status: "success", variant: "subtle", title: "Request cancelled" }),
      )
      .catch(() =>
        toast({
          status: "error",
          variant: "subtle",
          title: "Couldn't cancel request",
        }),
      );
  };

  const clickable = Boolean(onOpen);

  return (
    <Box
      flex="0 0 300px"
      bg="neutral.25"
      borderRadius="component.2xl" // 12px (.card)
      p="lg" // 16px
      display="flex"
      flexDirection="column"
      gap="md" // 12px
      boxShadow="elevLow"
      {...(clickable
        ? {
            role: "button",
            cursor: "pointer",
            transition: "box-shadow 120ms ease-out, transform 120ms ease-out",
            _active: { transform: "translateY(-1px) scale(0.99)" },
            onClick: () => onOpen!(r),
          }
        : {})}
      sx={{
        scrollSnapAlign: "start",
        // Gate the hover lift so a tap on a touch device doesn't leave a card
        // stuck in the raised state.
        ...(clickable
          ? {
              "@media (hover: hover) and (pointer: fine)": {
                "&:hover": { boxShadow: "elevHigh", transform: "translateY(-2px)" },
              },
            }
          : {}),
      }}
    >
      <Flex align="center" justify="space-between" gap="sm">
        <AppLogo size="md" name={logoName} logoUrl={logoUrl} />
        <ForTag recipient={r.onBehalfOf} me={me} />
      </Flex>

      <Box>
        <Box fontSize="15px" color="neutral.700" fontWeight={400} lineHeight="1.3">
          {appName && (
            <>
              {appName}{" "}
              <Box as="span" color="neutral.500">
                /
              </Box>{" "}
            </>
          )}
          <Box as="span" color="neutral.950" fontWeight={600}>
            {itemName}
          </Box>
        </Box>
        <Box
          fontSize="smd" // 13px
          color="neutral.700"
          mt="2xs"
          sx={{ fontVariantNumeric: "tabular-nums" }}
        >
          {isDraft ? "Not sent until you sign it" : sentLabel(r.createdAt)}
        </Box>
      </Box>

      {/* foot — pinned to the bottom of the card */}
      <Flex align="center" justify="space-between" gap="sm" mt="auto">
        {isDraft ? (
          <Flex align="center" gap="1xs" fontSize="xs" minW={0}>
            <Icon as={TbSignature} boxSize="14px" color="warning.600" flexShrink={0} />
            <Box
              as="span"
              fontWeight={600}
              color="warning.800"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              Needs your signature
            </Box>
          </Flex>
        ) : isPending ? (
          <Flex align="center" gap="1xs" fontSize="xs" color="neutral.700" minW={0}>
            <Icon as={TbShieldCheck} boxSize="14px" color="warning.600" flexShrink={0} />
            <Box as="span" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              {awaitingName ? (
                <>
                  Awaiting{" "}
                  <Box as="span" fontWeight={600} color="neutral.900">
                    {awaitingName}
                  </Box>
                </>
              ) : (
                "Awaiting approval"
              )}
            </Box>
          </Flex>
        ) : (
          // Past the approval gate — surface the approved state clearly, with a
          // live spinner for the in-progress provisioning (Chakra's Spinner
          // actually spins; a stringified-keyframe icon would dedupe away).
          <Flex align="center" gap="1xs" fontSize="xs" minW={0}>
            <Icon as={TbCircleCheck} boxSize="15px" color="success.600" flexShrink={0} />
            <Box as="span" fontWeight={600} color="success.700" flexShrink={0}>
              Approved
            </Box>
            <Spinner
              boxSize="12px"
              thickness="2px"
              speed="0.65s"
              color="azurite.600"
              emptyColor="neutral.200"
              ml="2px"
              flexShrink={0}
            />
            <Box
              as="span"
              color="neutral.700"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              Setting up access…
            </Box>
          </Flex>
        )}

        <Flex
          align="center"
          gap="2xs"
          flexShrink={0}
          onClick={(e) => e.stopPropagation()}
        >
          {isDraft && (
            <UtilityButton
              size="sm"
              treatment="subtle"
              sentiment="azurite"
              isDisabled={startingSignature}
              onClick={() => sign(r.id)}
              leftIcon={<Icon as={TbSignature} boxSize="14px" />}
            >
              Sign
            </UtilityButton>
          )}
          {isPending && (
            <UtilityButton
              size="sm"
              treatment="subtle"
              sentiment={isNudged ? "neutral" : "azurite"}
              isDisabled={isNudged || nudging}
              onClick={doNudge}
              leftIcon={<Icon as={isNudged ? TbCheck : TbBell} boxSize="14px" />}
            >
              {isNudged ? "Nudged" : "Nudge"}
            </UtilityButton>
          )}
          <UtilityButton
            size="sm"
            treatment="subtle"
            sentiment="negative"
            iconOnly
            aria-label="Cancel request"
            title="Cancel request"
            isDisabled={cancelling}
            onClick={() => setConfirmingCancel(true)}
          >
            <Icon as={TbTrash} boxSize="14px" />
          </UtilityButton>
        </Flex>
      </Flex>

      {confirmingCancel && (
        <ConfirmModal
          title="Cancel this request?"
          body={`This withdraws your access request for ${itemName}. You can request it again later.`}
          confirmLabel="Cancel request"
          // "Cancel" next to "Cancel request" reads as two ways to cancel the
          // request; name the dismiss action after what it actually does.
          cancelLabel="Keep request"
          confirmTone="danger"
          onConfirm={() => {
            setConfirmingCancel(false);
            doCancel();
          }}
          onCancel={() => setConfirmingCancel(false)}
        />
      )}
    </Box>
  );
};

const AwaitingApprovalRail = ({
  requests,
  me,
  onOpen,
}: {
  requests: MFMyActiveRequest[];
  me?: RailMember;
  onOpen?: (request: MFMyActiveRequest) => void;
}) => {
  if (!requests.length) return null;
  // Drafts first: they are the only cards waiting on the member themselves.
  const ordered = [...requests].sort(
    (a, b) => Number(needsSignature(b)) - Number(needsSignature(a)),
  );

  return (
    <Box mb="2xl">
      <Box
        fontFamily="body"
        fontSize="md"
        fontWeight={600}
        color="neutral.900"
        mb="md"
      >
        Awaiting approval
      </Box>
      {/* Bleed left into the page gutter and further right so cards can scroll
          off the edge; the padding restores breathing room for drop shadows. */}
      <Flex
        gap="md"
        overflowX="auto"
        overflowY="hidden"
        mt="-8px"
        mb="-8px"
        ml="-16px"
        mr={{ base: "-16px", md: "-40px" }}
        pt="8px"
        pb="lg"
        pl="lg"
        pr={{ base: "lg", md: "3xl" }}
        sx={{
          scrollSnapType: "x proximity",
          scrollPaddingLeft: "16px",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "thin",
        }}
      >
        {ordered.map((r) => (
          <AwaitingCard key={r.id} request={r} me={me} onOpen={onOpen} />
        ))}
      </Flex>
    </Box>
  );
};

export default AwaitingApprovalRail;
